import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import useUrlPagination from '../../utils/useUrlPagination';

// Simple activity log for admins: one plain sentence per action, filter by day/user, with a Detail button.
const MODULE_LABELS = {
    aduan: 'Aduan',
    kes: 'Kes',
    'i-waran': 'Waran',
    temujanji: 'Temujanji',
    'arahan-beredar': 'Arahan Beredar',
};

const ACTIONS = {
    created: { verb: 'mencipta', tone: 'good' },
    updated: { verb: 'mengemas kini', tone: 'info' },
    deleted: { verb: 'memadam', tone: 'bad' },
    export_pdf: { verb: 'mengeksport PDF', tone: 'info' },
    restored: { verb: 'memulihkan', tone: 'good' },
    finalized: { verb: 'memuktamadkan', tone: 'info' },
    unfinalized: { verb: 'membuka kunci', tone: 'info' },
};

const todayStr = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const shiftDay = (base, days) => {
    const d = new Date(`${base}T00:00:00`);
    d.setDate(d.getDate() + days);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const actionOf = (event) => ACTIONS[event] || { verb: (event || '-').replace(/_/g, ' '), tone: 'info' };
const entityLabel = (log) => MODULE_LABELS[log.module] || log.entity || 'rekod';
const referenceOf = (log) => log.complaint_reference_no || (log.auditable_id ? `#${log.auditable_id}` : '');

const sentenceOf = (log) => {
    const who = log.user?.name || 'Pengguna sistem';
    const { verb } = actionOf(log.event);
    return `${who} ${verb} ${entityLabel(log)} ${referenceOf(log)}`.trim();
};

const ActivityLogView = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const { page, setPage, perPage } = useUrlPagination(20);
    const [day, setDay] = useState(todayStr());
    const [userId, setUserId] = useState('');
    const [rows, setRows] = useState([]);
    const [users, setUsers] = useState([]);
    const [meta, setMeta] = useState({ total: 0, last_page: 1 });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [detail, setDetail] = useState(null);

    const params = useMemo(() => ({
        page,
        per_page: perPage,
        ...(day ? { date_from: day, date_to: day } : {}),
        ...(userId ? { user_id: userId } : {}),
    }), [page, perPage, day, userId]);

    const load = useCallback(() => {
        if (!apiUrl) { setError('API URL tidak diset.'); setIsLoading(false); return; }
        setIsLoading(true);
        setError('');
        axios.get(`${apiUrl}/audit-trail`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            params,
        })
            .then((response) => {
                setRows(response?.data?.data || []);
                setMeta(response?.data?.meta || { total: 0, last_page: 1 });
                if (response?.data?.filters?.users) setUsers(response.data.filters.users);
            })
            .catch((err) => setError(err?.response?.data?.message || 'Gagal memuatkan log aktiviti.'))
            .finally(() => setIsLoading(false));
    }, [apiUrl, token, params]);

    useEffect(() => { load(); }, [load]);

    const setDayAndReset = (value) => { setDay(value); setPage(1); };

    return (
        <div className="app-section">
            <div className="app-section-header">
                <div>
                    <div className="app-section-eyebrow">SYSTEM SETTING</div>
                    <h2>Log Aktiviti</h2>
                    <p>Aktiviti yang dilakukan oleh pengguna, ditapis ikut hari. Klik Detail untuk melihat maklumat penuh.</p>
                </div>
            </div>

            <div className="app-card">
                <div className="app-tab-panel">
                    <div className="app-form-grid">
                        <label className="app-form-field">
                            <span>Hari</span>
                            <input id="activity-day" type="date" value={day} max={todayStr()} onChange={(e) => setDayAndReset(e.target.value)} />
                        </label>
                        <label className="app-form-field">
                            <span>Pengguna</span>
                            <select id="activity-user" value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1); }}>
                                <option value="">Semua pengguna</option>
                                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </label>
                        <div className="app-form-actions app-span-full">
                            <button type="button" className="app-button app-button-ghost" onClick={() => setDayAndReset(todayStr())}>Hari ini</button>
                            <button type="button" className="app-button app-button-ghost" onClick={() => setDayAndReset(shiftDay(todayStr(), -1))}>Semalam</button>
                            <button type="button" className="app-button app-button-ghost" onClick={() => { setDay(''); setPage(1); }}>Semua tarikh</button>
                        </div>
                    </div>
                    <p className="app-muted" style={{ margin: '.5rem 0 0', fontSize: '.85rem' }}>
                        {meta.total} aktiviti{day ? ` pada ${day}` : ''}{userId ? ' untuk pengguna dipilih' : ''}.
                    </p>

                    {error && <div className="app-empty">{error}</div>}
                    {isLoading && <div className="app-empty">Memuatkan log aktiviti...</div>}
                    {!isLoading && !error && rows.length === 0 && <div className="app-empty">Tiada aktiviti untuk tapisan ini.</div>}

                    {!isLoading && !error && rows.length > 0 && (
                        <div className="app-activity-list">
                            {rows.map((log) => {
                                const action = actionOf(log.event);
                                return (
                                    <div className="app-activity-row" key={log.id}>
                                        <span className={`app-activity-dot tone-${action.tone}`} aria-hidden="true"></span>
                                        <div className="app-activity-main">
                                            <p className="app-activity-text">{sentenceOf(log)}</p>
                                            <span className="app-activity-meta">{log.created_at_human} · {log.user?.email || 'sistem'}</span>
                                        </div>
                                        <button type="button" className="app-button app-button-ghost app-button-sm" onClick={() => setDetail(log)}>
                                            Detail
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {!isLoading && !error && meta.last_page > 1 && (
                        <div className="app-activity-pager">
                            <button type="button" className="app-button app-button-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Sebelum</button>
                            <span>Halaman {page} / {meta.last_page}</span>
                            <button type="button" className="app-button app-button-ghost" disabled={page >= meta.last_page} onClick={() => setPage(page + 1)}>Seterusnya</button>
                        </div>
                    )}
                </div>
            </div>

            {detail && (
                <div className="app-modal" role="dialog" aria-modal="true">
                    <div className="app-modal-backdrop" onClick={() => setDetail(null)}></div>
                    <div className="app-modal-content">
                        <div className="app-modal-header">
                            <div>
                                <h4>Detail Aktiviti</h4>
                                <p>{sentenceOf(detail)}</p>
                            </div>
                            <button className="app-modal-close" onClick={() => setDetail(null)} aria-label="Tutup">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="app-detail-body">
                            <div className="app-kv"><span className="app-kv-label">Masa</span><span className="app-kv-value">{detail.created_at_human}</span></div>
                            <div className="app-kv"><span className="app-kv-label">Pengguna</span><span className="app-kv-value">{detail.user?.name || '-'} ({detail.user?.email || '-'})</span></div>
                            <div className="app-kv"><span className="app-kv-label">No. Akaun</span><span className="app-kv-value">{detail.user?.account_no || '-'}</span></div>
                            <div className="app-kv"><span className="app-kv-label">Modul</span><span className="app-kv-value">{entityLabel(detail)} ({detail.module})</span></div>
                            <div className="app-kv"><span className="app-kv-label">Tindakan</span><span className="app-kv-value">{actionOf(detail.event).verb} ({detail.event})</span></div>
                            <div className="app-kv"><span className="app-kv-label">Rekod</span><span className="app-kv-value">{detail.entity} {referenceOf(detail)}</span></div>
                            <div className="app-kv"><span className="app-kv-label">Alamat IP</span><span className="app-kv-value">{detail.ip || '-'}</span></div>

                            {(detail.changed_keys || []).length > 0 && (
                                <div className="app-activity-changes">
                                    <h5>Perubahan medan</h5>
                                    <div className="app-inline-table app-inline-clean">
                                        <div className="app-inline-table-header" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                                            <span>Medan</span><span>Sebelum</span><span>Selepas</span>
                                        </div>
                                        {detail.changed_keys.map((key) => (
                                            <div className="app-inline-table-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }} key={key}>
                                                <span>{String(key).replace(/_/g, ' ')}</span>
                                                <span className="app-activity-old">{formatValue(detail.old_values?.[key])}</span>
                                                <span className="app-activity-new">{formatValue(detail.new_values?.[key])}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="app-modal-footer">
                            <button type="button" className="app-button app-button-ghost" onClick={() => setDetail(null)}>Tutup</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function formatValue(value) {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

export default ActivityLogView;
